import { c as _c } from "react/compiler-runtime";
import { cn } from "@dc/ui/lib/utils";
import { Fragment, type PropsWithChildren, useId } from "react";
import { MagniferLinear } from "solar-icon-set";
import { Button } from "../Button";
import { Divider } from "../Divider";
import { Input } from "../Input";
import { ScrollShadow } from "../ScrollShadow";
import { useFilterPanelFieldSearch } from "./hooks/useFiltersPanelFieldSearch";
import type { FiltersPanelField, FiltersPanelProps, FiltersPanelSearchInputProps, FiltersPanelSectionType } from "./types";
export function FiltersPanelHeader(props) {
  const $ = _c(9);
  const {
    title,
    resetLabel: t0,
    resetAriaLabel: t1,
    onReset
  } = props;
  const resetLabel = t0 === undefined ? "Reset" : t0;
  const resetAriaLabel = t1 === undefined ? "Reset filters" : t1;
  let t2;
  if ($[0] !== title) {
    t2 = <h2 className="font-medium text-lg">{title}</h2>;
    $[0] = title;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onReset || $[3] !== resetAriaLabel || $[4] !== resetLabel) {
    t3 = onReset && <Button color="secondary" variant="light" onPress={onReset} aria-label={resetAriaLabel} size="sm">{resetLabel}</Button>;
    $[2] = onReset;
    $[3] = resetAriaLabel;
    $[4] = resetLabel;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="mb-2 flex items-center justify-between gap-2 pr-4 md:pr-0">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}

/**
 * Construction taken from React Aria menu section, which hero ui is based on
 *
 * https://github.com/adobe/react-spectrum/blob/50e009edb5a242356c5d73bb43cca870d62f8b87/packages/%40react-aria/menu/src/useMenuSection.ts#L40
 */
export function FiltersPanelSection(props) {
  const $ = _c(13);
  const {
    children,
    title,
    headingClassName
  } = props;
  const headingId = useId();
  let t0;
  if ($[0] !== headingClassName) {
    t0 = cn("px-2 py-3 text-text-body-neutral-onMatch-muted text-xs", headingClassName);
    $[0] = headingClassName;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== headingId || $[3] !== t0 || $[4] !== title) {
    t1 = <div role="presentation" id={headingId} className={t0}>{title}</div>;
    $[2] = headingId;
    $[3] = t0;
    $[4] = title;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== headingId || $[8] !== title) {
    t2 = <fieldset aria-label={title} aria-labelledby={headingId} className="ml-2 grid gap-1">{children}</fieldset>;
    $[6] = children;
    $[7] = headingId;
    $[8] = title;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t1 || $[11] !== t2) {
    t3 = <div className="grid gap-1">{t1}{t2}</div>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
export function FiltersPanelContent(props) {
  const $ = _c(17);
  const {
    searchValue,
    fieldMap,
    sections,
    emptyStateMessage,
    renderField,
    className
  } = props;
  const matchingFields = useFilterPanelFieldSearch(fieldMap, searchValue);
  let T0;
  let t0;
  let t1;
  if ($[0] !== className || $[1] !== emptyStateMessage || $[2] !== matchingFields || $[3] !== renderField || $[4] !== searchValue || $[5] !== sections) {
    let t2;
    if ($[9] !== matchingFields) {
      t2 = section => ({
        ...section,
        fields: section.fields.filter(f => matchingFields.has(f.id))
      });
      $[9] = matchingFields;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    const visibleSections = sections.map(t2).filter(_temp);
    const showEmptyState = visibleSections.length === 0 && searchValue && searchValue.trim().length > 0;
    T0 = ScrollShadow;
    if ($[11] !== className) {
      t0 = cn("overflow-y-auto pr-1", className);
      $[11] = className;
      $[12] = t0;
    } else {
      t0 = $[12];
    }
    t1 = showEmptyState ? <p className="text-sm text-text-default-muted">{emptyStateMessage}</p> : visibleSections.map(section_1 => <FiltersPanelSection key={section_1.title} title={section_1.title}>{section_1.fields.map(field => <Fragment key={field.id}>{renderField(field)}</Fragment>)}</FiltersPanelSection>);
    $[0] = className;
    $[1] = emptyStateMessage;
    $[2] = matchingFields;
    $[3] = renderField;
    $[4] = searchValue;
    $[5] = sections;
    $[6] = T0;
    $[7] = t0;
    $[8] = t1;
  } else {
    T0 = $[6];
    t0 = $[7];
    t1 = $[8];
  }
  let t2;
  if ($[13] !== T0 || $[14] !== t0 || $[15] !== t1) {
    t2 = <T0 className={t0}>{t1}</T0>;
    $[13] = T0;
    $[14] = t0;
    $[15] = t1;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  return t2;
}
function _temp(section_0) {
  return section_0.fields.length > 0;
}
export function FiltersPanel(props) {
  const $ = _c(5);
  const {
    className,
    children
  } = props;
  let t0;
  if ($[0] !== className) {
    t0 = cn("flex flex-col gap-1 p-4", className);
    $[0] = className;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== children || $[3] !== t0) {
    t1 = <ScrollShadow hideScrollBar={true}><div className={t0}>{children}</div></ScrollShadow>;
    $[2] = children;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}

/**
 * @deprecated Use the composable building blocks (`FiltersPanel`, `FiltersPanelSearchInput`,
 * `FiltersPanelContent`) directly instead. See the ADR "ProjectFilters Responsive Menu
 * Restructure" in Notion: https://app.notion.com/p/3d7b2e092e53816081c8efc1c2fa17a6
 */
const FiltersPanelLegacy = t0 => {
  const $ = _c(35);
  const {
    headerTitle: t1,
    resetLabel: t2,
    onReset,
    searchValue,
    onSearchValueChange,
    searchPlaceholder: t3,
    searchAriaLabel: t4,
    sections,
    emptyStateMessage: t5,
    onCancel,
    cancelLabel: t6,
    onApply,
    applyLabel: t7,
    isApplyDisabled: t8,
    className: t9,
    footerClassName: t10,
    fieldMap,
    renderField
  } = t0;
  const headerTitle = t1 === undefined ? "Filters" : t1;
  const resetLabel = t2 === undefined ? "Reset" : t2;
  const searchPlaceholder = t3 === undefined ? "Search" : t3;
  const searchAriaLabel = t4 === undefined ? "Search filters" : t4;
  const emptyStateMessage = t5 === undefined ? "No filters match your search." : t5;
  const cancelLabel = t6 === undefined ? "Cancel" : t6;
  const applyLabel = t7 === undefined ? "Apply Filters" : t7;
  const isApplyDisabled = t8 === undefined ? false : t8;
  const className = t9 === undefined ? "" : t9;
  const footerClassName = t10 === undefined ? "" : t10;
  let t11;
  if ($[0] !== headerTitle || $[1] !== onReset || $[2] !== resetLabel) {
    t11 = <FiltersPanelHeader title={headerTitle} resetLabel={resetLabel} onReset={onReset} />;
    $[0] = headerTitle;
    $[1] = onReset;
    $[2] = resetLabel;
    $[3] = t11;
  } else {
    t11 = $[3];
  }
  let t12;
  if ($[4] !== onSearchValueChange || $[5] !== searchAriaLabel || $[6] !== searchPlaceholder || $[7] !== searchValue) {
    t12 = <FiltersPanelSearchInput placeholder={searchPlaceholder} value={searchValue} onValueChange={onSearchValueChange} aria-label={searchAriaLabel} />;
    $[4] = onSearchValueChange;
    $[5] = searchAriaLabel;
    $[6] = searchPlaceholder;
    $[7] = searchValue;
    $[8] = t12;
  } else {
    t12 = $[8];
  }
  let t13;
  if ($[9] !== emptyStateMessage || $[10] !== fieldMap || $[11] !== renderField || $[12] !== searchValue || $[13] !== sections) {
    t13 = <FiltersPanelContent className="mb-2" searchValue={searchValue} fieldMap={fieldMap} sections={sections} renderField={renderField} emptyStateMessage={emptyStateMessage} />;
    $[9] = emptyStateMessage;
    $[10] = fieldMap;
    $[11] = renderField;
    $[12] = searchValue;
    $[13] = sections;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Divider className="mb-2 bg-default-100" />;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== footerClassName) {
    t15 = cn("flex justify-end gap-2", footerClassName);
    $[16] = footerClassName;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== cancelLabel || $[19] !== onCancel) {
    t16 = onCancel && <Button color="secondary" variant="bordered" onPress={onCancel} size="sm">{cancelLabel}</Button>;
    $[18] = cancelLabel;
    $[19] = onCancel;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== applyLabel || $[22] !== isApplyDisabled || $[23] !== onApply) {
    t17 = onApply && <Button color="secondary" onPress={onApply} isDisabled={isApplyDisabled} size="sm">{applyLabel}</Button>;
    $[21] = applyLabel;
    $[22] = isApplyDisabled;
    $[23] = onApply;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== t15 || $[26] !== t16 || $[27] !== t17) {
    t18 = <div className={t15}>{t16}{t17}</div>;
    $[25] = t15;
    $[26] = t16;
    $[27] = t17;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== className || $[30] !== t11 || $[31] !== t12 || $[32] !== t13 || $[33] !== t18) {
    t19 = <FiltersPanel className={className}>{t11}{t12}{t13}{t14}{t18}</FiltersPanel>;
    $[29] = className;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  return t19;
};
export function FiltersPanelSearchInput(t0) {
  const $ = _c(6);
  const {
    value,
    onValueChange,
    placeholder: t1,
    "aria-label": t2
  } = t0;
  const placeholder = t1 === undefined ? "Search" : t1;
  const ariaLabel = t2 === undefined ? "Search" : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <MagniferLinear className="size-4" color="var(--color-icon-neutral-onMatch-dimmed)" />;
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== ariaLabel || $[2] !== onValueChange || $[3] !== placeholder || $[4] !== value) {
    t4 = <Input placeholder={placeholder} size="sm" value={value} onValueChange={onValueChange} startContent={t3} isClearable={true} variant="bordered" aria-label={ariaLabel} />;
    $[1] = ariaLabel;
    $[2] = onValueChange;
    $[3] = placeholder;
    $[4] = value;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
export default FiltersPanelLegacy;
export type { FiltersPanelProps, FiltersPanelSectionType } from "./types";